import { Box } from '@chakra-ui/react';
import type { MultiValue, SingleValue } from 'chakra-react-select';
import type React from 'react';
import { useEffect, useMemo } from 'react';
import FaceliftInput from '@/components/Atoms/FaceliftInput';
import FaceliftSelect from '@/components/Atoms/FaceliftSelect/FaceliftSelect';
import type { SelectItem } from '@/components/Atoms/FaceliftSelect/FaceliftSelect.types';
import FaceliftText from '@/components/Atoms/FaceliftText';
import FaceliftTextArea from '@/components/Atoms/FaceliftTextArea';
import type { BVFormData } from '@/components/Templates/BenefitVerification/BenefitVerificationForm/BenefitVerification';
import { useGetBvProducts } from '@/hooks/service-hooks/products';
import { useAuthStore } from '@/store';
import type { FormMethods } from '@/types/form';
import type { BvProduct } from '@/types/product';

export interface ProductData {
	productSelection: string;
	notes: string;
}

export interface ProductProps {
	formMethods: FormMethods<BVFormData>;
}

const Product: React.FC<ProductProps> = ({ formMethods }) => {
	const { watch, errors, setValue, getValues, setError } = formMethods;

	const user = useAuthStore((state) => state.user?.userInformation);
	const userCommissionableProducts = user?.commissionableProducts || [];

	const { data: bvProducts, isFetching } = useGetBvProducts({
		clinicId: ((getValues('clinic') as SelectItem)?.value as string) || '',
		userPort: user?.portsAvailable || 'WoundCare+',
		enabled: true,
	});

	const accountProducts = useMemo(() => {
		const clinicId = (watch('clinic') as SelectItem)?.value as string | undefined;

		const account = bvProducts?.items?.find(
			(el) => el.accountId.toLowerCase() === clinicId?.toLowerCase(),
		);

		if (!account) return [];

		const products = account?.accountBvProducts?.map((product: BvProduct) => ({
			label: product.text,
			value: product.value,
			qCode: product.qCode,
		}));

		const checkedUserProducts = userCommissionableProducts.filter((p) => p.checked);

		// If nothing is checked, just return all products
		if (!checkedUserProducts.length) return products;

		const checkedQCodes = new Set(checkedUserProducts.map((p) => p.qCode));
		const starredQCodes = new Set(
			userCommissionableProducts.filter((p) => p.starred).map((p) => p.qCode),
		);

		const visibleProducts = products.filter((product) =>
			checkedQCodes.has(product.qCode),
		);

		// If no visible products, just return empty array
		if (!visibleProducts.length) return visibleProducts;

		// Only sort if at least one of the visible products is starred
		const hasStarred = visibleProducts.some((p) => starredQCodes.has(p.qCode));
		if (!hasStarred) return visibleProducts;

		return [...visibleProducts].sort((a, b) => {
			const aStarred = starredQCodes.has(a.qCode);
			const bStarred = starredQCodes.has(b.qCode);
			if (aStarred && !bStarred) return -1;
			if (!aStarred && bStarred) return 1;
			return 0;
		});
	}, [bvProducts]);

	useEffect(() => {
		if (
			accountProducts?.length &&
			!watch('productSelectedNames') &&
			watch('ta_productselection')
		) {
			const selectedProducts = accountProducts.filter((item) =>
				watch('ta_productselection')?.includes(String(item.value)),
			);

			if(!selectedProducts.length) {
				return;
			}
			setValue(
				'productSelectedNames',
				selectedProducts.map((item) => String(item.label)).join(', '),
				{ shouldDirty: false },
			);
		}
	}, [accountProducts]);

	return (
		<Box>
			<FaceliftText
				fontSize={{ base: 'sm', md: 'md' }}
				fontWeight="semibold"
				mb={{ base: 4, md: 6 }}
				fontStyle="italic"
			>
				Product
			</FaceliftText>

			{/* Product Selection  TODO: Add Multi Select */}
			<Box mb={{ base: 6, md: 10 }}>
				<FaceliftSelect
					limit={15}
					topLabel="Product (s)"
					placeholder="Select Products"
					value={
						accountProducts?.filter((item) =>
							watch('ta_productselection')?.includes(String(item.value)),
						) || null
					}
					onChange={(newValue: MultiValue<SelectItem>) => {
						setValue(
							'ta_productselection',
							newValue.map((item) => String(item.value)).join(','),
							{ shouldDirty: true },
						);
						setValue(
							'productSelectedNames',
							newValue.map((item) => String(item.label)).join(', '),
							{ shouldDirty: true },
						);
						setError('ta_productselection', { type: 'manual', message: '' });
					}}
					items={(accountProducts as SelectItem[]) || []}
					loading={isFetching}
					error={errors?.ta_productselection?.message}
					required
					isMulti={true}
				/>
			</Box>

			{/* TODO: Add Text Area Component */}
			<Box>
				<FaceliftTextArea
					topLabel="Notes"
					value={watch('ta_noteshartreview')}
					onChange={(e) => setValue('ta_noteshartreview', e.target.value, { shouldDirty: true })}
					placeholder="Type Here"
				/>
			</Box>
		</Box>
	);
};

export default Product;
